import { ChatThreadClientProvider, useChatClient } from "@azure/communication-react";
import React, { useEffect, useMemo, useState } from "react";
import { Stack } from "@fluentui/react";
import { fetchThreads } from "../../utils";
import { usePcpChatThreadState } from "../../store/slices/chatThreadStateSlice";

interface AllChatThreadsProviderProps {
    renderChatThread: (threadId: string) => JSX.Element;
}

export function AllChatThreadsProvider(props: AllChatThreadsProviderProps): JSX.Element {
    const chatClient = useChatClient();

    const chatState = chatClient.getState();
    const threads = chatState.threads;

    const threadState = usePcpChatThreadState();
    const activeThreadId = useMemo(() => threadState.activeThread, [threadState.activeThread]);


    useEffect(() => {
        // fetch once on mount
        fetchThreads(chatClient).catch(console.error);
    }, [chatClient]);

    // Only the active thread is ever mounted, so render it directly instead of
    // iterating over every thread on each render.
    const activeThread = activeThreadId ? threads[activeThreadId] : undefined;

    return (
        <Stack verticalFill>
            {activeThread && (
                <Stack.Item verticalFill key={activeThread.threadId}>
                    <ChatThreadClientProviderTrampoline threadId={activeThread.threadId} renderChatThread={props.renderChatThread} />
                </Stack.Item>
            )}
        </Stack>
    );
}

interface ChatThreadClientProviderTrampolineProps {
    threadId: string;
    renderChatThread: (threadId: string) => JSX.Element;
}

export const ChatThreadClientProviderTrampoline = (props: ChatThreadClientProviderTrampolineProps) => {
    const { threadId, renderChatThread } = props;
    const chatClient = useChatClient();

    const threadClient = useMemo(
        () => chatClient.getChatThreadClient(threadId),
        [chatClient, threadId],
    );

    const [topic, setTopic] = useState<string | undefined>(undefined);
    useEffect(() => {
        threadClient.getProperties()
            .then((threadProps) => setTopic(threadProps.topic))
            .catch(console.error);
    }, [threadClient]);

    return (
        <>
            {threadClient && (
                <ChatThreadClientProvider chatThreadClient={threadClient}>
                    {renderChatThread(threadId)}
                </ChatThreadClientProvider>
            )}
        </>
    );
};
